import React, { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { ErrorModal } from '~/components/Errors';
import { ServicesContext } from '~/services';
import { enumValue } from '~/util';

export function OHIDIntegration() {
    const services = React.useContext(ServicesContext);
    const location = useLocation();
    const ohidLink = services.user.getExternalAccount('One Healthcare ID');
    const [loading, setLoading] = React.useState(false);
    const [showError, setShowError] = React.useState(false);
    const [error, setError] = React.useState<React.ReactNode>();

    function linkOHID() {
        setLoading(true);
        window.open('/oidc', '_self');
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const ohidLinkStatus = queryParams.get('oidc-linking-status');
        const ohidLinkCode = queryParams.get('response-code');
        const duplicateLinkError = enumValue([
            'response',
            'code',
            'OIDC_LINKED_TO_ANOTHER_USER_ERROR',
        ]);
        const connectionError = enumValue(['response', 'code', 'OIDC_CONNECTION_ERROR']);
        if (ohidLinkStatus === 'success') {
            services.alert.set(
                'Your One Healthcare ID has been successfully linked to your Zephyr account. You can now log in with “Agree & Continue with One Healthcare ID” on the Zephyr login page.',
                'success'
            );
        } else if (ohidLinkCode === String(duplicateLinkError)) {
            setError(
                <div>
                    <p>
                        This One Healthcare ID is already linked to another Zephyr account. Please
                        contact support at{' '}
                        <a href="mailto:entitlement@wellmed.net">entitlement@wellmed.net</a> for
                        assistance.
                    </p>
                    Error: {duplicateLinkError}
                </div>
            );
            setShowError(true);
        } else if (ohidLinkCode === String(connectionError)) {
            setError(
                <div>
                    <p>
                        Sorry, an error occurred while connecting to One Healthcare ID. Please try
                        again. If this problem persists, please contact One Healthcare ID support.
                    </p>
                    Error: {connectionError}
                </div>
            );
            setShowError(true);
        }
    }, [services.alert, location.search]);

    return (
        <div className="row align-items-center">
            <div className="col-3 text-end">OHID</div>
            <div className="col-3">
                {ohidLink ? (
                    <>
                        <span className="me-2">Enabled</span>
                        <i
                            className="fa-solid fa-check-circle text-success"
                            title="Your One HealthCare ID account is linked to your Zephyr account"
                        />
                    </>
                ) : (
                    <span>Off</span>
                )}
            </div>
            <div className="col-6 text-end text-nowrap">
                {!ohidLink && (
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={linkOHID}
                        disabled={loading}
                    >
                        {loading && (
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                                role="status"
                                className="me-1"
                            />
                        )}
                        Link Account
                    </button>
                )}
            </div>
            <ErrorModal
                show={showError}
                showSubmit={false}
                handleClose={() => setShowError(false)}
                title="One Healthcare ID Error"
                description={error}
            />
        </div>
    );
}
